import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Box, Button, List, TextField, Typography } from '@mui/material';
import { Plus } from '@phosphor-icons/react';
import { UploadItemAccordion } from './UploadItemAccordion';
import { VectorMetadataItem } from './VectorMetadataItem';

import type { VectorMetadata } from '../../../types/vector';

interface UploadItemMetadatasProps {
  id: string;
  fileMetadatas: Array<VectorMetadata>;
}

const metadataSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  value: z.string().min(1, { message: 'Value is required' }),
});

type MetadataData = z.infer<typeof metadataSchema>;

function UploadItemMetadatas({ id, fileMetadatas }: UploadItemMetadatasProps) {
  const { onMetadataCreate, onMetadataDelete } = useVectorModal();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      value: '',
    },
    resolver: zodResolver(metadataSchema),
  });

  const [expanded, setExpanded] = useState(false);

  const handleExpandedToggle = () => {
    setExpanded(prev => !prev);
  };

  const handleMetadataAdd = ({ name, value }: MetadataData) => {
    const nameFormatted = name.toLowerCase().split(' ').join('_');

    const hasDuplicateKeyName = fileMetadatas.find(
      metadata => metadata.name === nameFormatted,
    );

    if (hasDuplicateKeyName !== undefined) {
      setError('name', { message: 'Label already exists' });
      return;
    }

    const newMetadata: VectorMetadata = {
      name: nameFormatted,
      value,
    };

    onMetadataCreate({ filesIds: [id], metadatas: [newMetadata] });
    reset();
  };

  const handleMetadataDelete = (metadataName: string) => {
    onMetadataDelete({ filesIds: [id], metadatas: [{ name: metadataName }] });
  };

  return (
    <UploadItemAccordion
      expanded={expanded}
      title="Labels"
      onExpandedToggle={handleExpandedToggle}
    >
      <List
        sx={{
          padding: 0,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        {fileMetadatas?.length ? (
          fileMetadatas.map(({ name, value }) => (
            <VectorMetadataItem
              key={name}
              name={name}
              value={value}
              onMetadataDelete={handleMetadataDelete}
            />
          ))
        ) : (
          <Box
            sx={{
              width: '100%',
              backgroundColor: '#fafafa',
              paddingInline: '0.5rem',
              paddingBlock: '0.375rem',

              borderRadius: '0.25rem',
              borderColor: '#d4d4d8',
              borderWidth: '1px',
              borderStyle: 'solid',

              textAlign: 'center',
            }}
          >
            <Typography color="#27272a">No label created</Typography>
          </Box>
        )}
      </List>

      <Box
        component="form"
        id={`add-new-label-${id}`}
        sx={{ '&>*+*': { marginBlockStart: '0.25rem !important' } }}
        onSubmit={handleSubmit(handleMetadataAdd)}
      >
        <Typography variant="h5" fontSize="1rem" color="black" fontWeight={700}>
          Add New Label
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <TextField
            placeholder="Type Key here"
            sx={{ flex: 1 }}
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name')}
          />

          <TextField
            placeholder="Type Value here"
            sx={{ flex: 1 }}
            error={!!errors.value}
            helperText={errors.value?.message}
            {...register('value')}
          />

          <Button type="submit" variant="outlined" form={`add-new-label-${id}`}>
            Add
            <Plus size={16} />
          </Button>
        </Box>
      </Box>
    </UploadItemAccordion>
  );
}

export { UploadItemMetadatas, metadataSchema };
